import { ConfigNode } from "protos/config/api/config_service";
import { Crop } from "protos/portal/veselka";
import { getNodeFromPath, getValue } from "./configs";
import { isBud, isReaper, isSimulatorOrMvs, isSlayer } from "./robots";
import { PortalLanguage, VESELKA_LANGUAGES } from "portal/i18nConstants";
import { sortBy } from "./arrays";
import { TFunction } from "i18next";

const SLAYER_CROP_IDS_PATH = "command/commander/crop_ids";
const BUD_CROP_IDS_PATH = `bud/commander/crop_ids`;

export type CropMapById = { [id: string]: Crop };

export interface DisplayCrop {
  localeName: string;
  carbonName: string;
  id: string;
  pinned: string;
  recommended: string;
  isEnabled: boolean;
}

export const cropToPortalCrop = (
  crop: Crop,
  lang: PortalLanguage
): DisplayCrop => ({
  id: crop.id,
  carbonName: crop.carbonName,
  localeName: getLocaleCropName(crop, lang),
  pinned: "",
  recommended: "",
  isEnabled: true,
});

export const getLocaleCropName = (
  crop: Crop,
  language: PortalLanguage
): string =>
  crop.translations.find(
    (trans) => trans.language === VESELKA_LANGUAGES[language]
  )?.name ?? crop.commonName;

export interface SortedCrops {
  pinnedAndEnabledCrops: number;
  archived: DisplayCrop[];
  enabled: DisplayCrop[];
  disabled: DisplayCrop[];
  all: DisplayCrop[];
}

export const getCrops = (
  serial: string | undefined,
  config: ConfigNode | undefined,
  allCrops: CropMapById | undefined,
  language: PortalLanguage,
  t: TFunction
): SortedCrops => {
  const result: SortedCrops = {
    pinnedAndEnabledCrops: 0,
    archived: [],
    enabled: [],
    disabled: [],
    all: [],
  };
  if (!serial) {
    return result;
  }
  let cropIdsNode: ConfigNode | undefined;
  if (isSlayer(serial) || isSimulatorOrMvs(serial) || isReaper(serial)) {
    cropIdsNode = getNodeFromPath(config, SLAYER_CROP_IDS_PATH);
  } else if (isBud(serial)) {
    cropIdsNode = getNodeFromPath(config, BUD_CROP_IDS_PATH);
  }
  if (!cropIdsNode) {
    return result;
  }
  for (const cropNode of cropIdsNode.children) {
    const veselkaCrop = allCrops ? allCrops[cropNode.name] : undefined;
    const carbonName =
      veselkaCrop?.carbonName ??
      getValue<string>(getNodeFromPath(cropNode, "carbon_name")) ??
      t("utils.descriptors.unknown");
    const localeName = veselkaCrop
      ? getLocaleCropName(veselkaCrop, language)
      : getValue<string>(getNodeFromPath(cropNode, "common_name")) ??
        t("utils.descriptors.unknown");
    const isEnabled =
      getValue<boolean>(getNodeFromPath(cropNode, "enabled")) ?? false;
    const pinned = getValue<string>(getNodeFromPath(cropNode, "pinned_model"));
    const recommended = getValue<string>(
      getNodeFromPath(cropNode, "recommended_model")
    );
    if (isEnabled && pinned) {
      result.pinnedAndEnabledCrops++;
    }
    const crop = {
      id: cropNode.name,
      carbonName,
      localeName,
      pinned: pinned ?? "",
      recommended: recommended ?? "",
      isEnabled,
    };
    if (isEnabled) {
      result.enabled.push(crop);
    } else if (veselkaCrop && !veselkaCrop.archived) {
      result.disabled.push(crop);
    } else {
      result.archived.push(crop);
    }
  }
  sortBy(result.enabled, "localeName");
  sortBy(result.disabled, "localeName");
  result.all = [...result.enabled, ...result.disabled];
  return result;
};

export const getConfigKey = (serial: string, cropId: string): string =>
  `${isBud(serial) ? BUD_CROP_IDS_PATH : SLAYER_CROP_IDS_PATH}/${cropId}`;
